import { Scope } from '@developerz/domain';
import { createSignal, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { useEntitlements } from '../lib/billing';
import { createMutation } from '../lib/mutation';
import {
  type OssVerificationDraft,
  submitOssVerification,
  useOssVerification,
} from '../lib/ossVerification';
import { useScopeVerdict } from '../lib/scope-gate';
import { EmptyState } from './EmptyState';
import { OssVerifyForm } from './OssVerifyForm';
import { OssVerifyStatus } from './OssVerifyStatus';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// "Apply for OSS Verified" (arc 101 slice 07, #68) — the dashboard path
// `docs/idea/billing.md` promised while the only real one was emailing
// oss@developerz.ai. Container: it owns the read, the POST, and which of the
// two presenters is on screen; neither presenter fetches.
//
// One form serves apply AND edit because the API replaces the open row on a
// re-POST — a second "edit application" surface would be the same three fields
// pointed at the same route.

export function OssVerifyPanel(props: {
  readonly token: string | null;
  /** Surface the submit outcome through the page's toast. */
  readonly onNotice?: (message: string, tone: 'success' | 'error') => void;
}): JSX.Element {
  const query = useOssVerification(props.token);
  const seats = useEntitlements(props.token);
  const [editing, setEditing] = createSignal(false);

  // The settled `write:billing` verdict (`useScopeVerdict`) — the same gate
  // that decides who is offered the seat cart, so both money surfaces can't
  // drift on who may act. It reads the `/v1/me` query directly (same key), so
  // no extra request. Three states, AgentModesPanel's rule: 'allowed' renders
  // the form; 'denied' the read-only note; 'unsettled' NEITHER — no "you
  // can't" line that flips a moment later.
  const writeVerdict = useScopeVerdict(Scope.WriteBilling, props.token);

  // Show the panel when there's something to ask for OR something to report.
  // Two cases sit it out: no session (both queries stay idle, so the panel would
  // be a permanently empty box next to Billing's own "sign in" state), and an
  // account already on the verified tier that never applied. A row of ANY status
  // still renders — it is the applicant's only receipt.
  const offer = (): boolean =>
    props.token !== null &&
    !(seats.data?.tier === 'oss_verified' && (query.data?.request ?? null) === null);

  /**
   * The application POST, on the one write kit (`lib/mutation.ts`).
   *
   * Migrated off its hand-rolled `setBusy` ladder for the property that ladder
   * did not have: a RE-ENTRANCY LOCK. `<OssVerifyForm/>` submits a `<form>`, so
   * Enter in the evidence field and a second click in the same tick both reach
   * this handler directly — `busy` only ever disabled the button, and a
   * `disabled` attribute is a rendering, not a lock. Two POSTs is not merely
   * wasteful here: the API REPLACES the open row on a re-POST, so the second one
   * overwrites the row the first just created.
   *
   * The refetch moves to `onSettled` — both outcomes, awaited INSIDE the pending
   * window — which is a deliberate widening of the old success-only refetch: a
   * refused submit is exactly when the applicant needs to see the row that is
   * actually stored, and the form stays disabled until it is on screen.
   */
  const apply = createMutation({
    call: (draft: OssVerificationDraft) => submitOssVerification(draft, props.token),
    fallback: t('oss_verify.submit_error'),
    onSuccess: () => {
      setEditing(false);
      props.onNotice?.(t('oss_verify.submitted_toast'), 'success');
    },
    // Reported through the PAGE's toast, not rendered here — so the panel reads
    // `onError` rather than `error()`, and `errorReason` (the kit's own) is what
    // lets the API's 422 `detail` outrank the fallback.
    onError: (message) => props.onNotice?.(message, 'error'),
    onSettled: () => query.refetch(),
  });

  return (
    <Show when={offer()}>
      <Panel title={t('oss_verify.heading')}>
        <p class="oss-verify__desc">{t('oss_verify.desc')}</p>
        <QueryBoundary query={query} loading={<Skeleton height="7rem" />}>
          {(view) => (
            <Show
              when={!editing() && view.request}
              fallback={
                <Switch>
                  <Match when={writeVerdict() === 'denied'}>
                    <EmptyState message={t('oss_verify.read_only')} />
                  </Match>
                  <Match when={writeVerdict() === 'allowed'}>
                    <OssVerifyForm
                      initial={view.request}
                      busy={apply.pending()}
                      onSubmit={(draft) => void apply.run(draft)}
                    />
                  </Match>
                </Switch>
              }
            >
              {(request) => (
                <OssVerifyStatus
                  request={request()}
                  canEdit={writeVerdict() === 'allowed' && request().status !== 'approved'}
                  onEdit={() => setEditing(true)}
                />
              )}
            </Show>
          )}
        </QueryBoundary>
      </Panel>
    </Show>
  );
}
